import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { TabsComponent } from '@enechain/ecloud-designsystem'

import { DsSelect, type DsSelectOption } from '~/components/common/DsSelect'
import {
  DateStepper,
  FilterSection,
  FilterToolbar,
} from '~/components/common/filters'
import { tabTriggerFontSize } from '~/constants/tabStyle'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import {
  type CivilDate,
  civilDateToDate,
  dateToCivilDate,
} from '~/models/civilDate'
import { MonthStepper } from '~/pages/SplitSupplyPlan/components/MonthStepper'
import {
  SECTION_TABS,
  type SectionTab,
  stepTarget,
} from '~/pages/SplitSupplyPlan/models/period'

const ALL_AREAS = 'all'

type Props = {
  tab: SectionTab
  onTabChange: (tab: SectionTab) => void
  /** 翌日は受渡日、 月間は対象月の 1 日。 */
  target: CivilDate
  onTargetChange: (target: CivilDate) => void
  /** 未指定 = すべてのエリア。 */
  area: AreaCode | undefined
  onAreaChange: (area: AreaCode | undefined) => void
  /** 今日 (JST)。 「今日」「今月」の送り先。 */
  today: CivilDate
}

/**
 * 分割供給計画の一覧の絞り込み: 断面 (翌日 / 月間) のタブ、 対象日 (月間は対象月)、 エリア。
 * 自社の通告値と他社の通告値の一覧で共通 (上段のタブで切り替えても断面・対象日・エリアは保たれる)。
 */
export const SectionFilterBar = ({
  tab,
  onTabChange,
  target,
  onTargetChange,
  area,
  onAreaChange,
  today,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const tabItems = SECTION_TABS.map((key) => ({
    key,
    label: t(`page:splitSupplyPlan.tab.${key}`),
  }))
  const areaOptions: DsSelectOption[] = [
    { value: ALL_AREAS, label: t('page:splitSupplyPlan.filter.areaAll') },
    ...AREA_CODE_OPTIONS.map((areaCode) => ({
      value: String(areaCode),
      label: convertAreaLabel(areaCode),
    })),
  ]

  return (
    <>
      <TabsComponent
        items={tabItems}
        onSelect={(key: SectionTab) => {
          onTabChange(key)
        }}
        selected={tab}
        tabSx={{ fontSize: tabTriggerFontSize.raw }}
      />

      <div style={{ position: 'relative', zIndex: 10 }}>
        <FilterToolbar>
          <FilterSection
            label={t(
              tab === 'monthly'
                ? 'page:splitSupplyPlan.filter.targetMonth'
                : 'page:splitSupplyPlan.filter.targetDate',
            )}
          >
            {tab === 'monthly' ? (
              <MonthStepper
                onNext={() => {
                  onTargetChange(stepTarget(tab, target, 1))
                }}
                onPrev={() => {
                  onTargetChange(stepTarget(tab, target, -1))
                }}
                onThisMonth={() => {
                  onTargetChange({
                    year: today.year,
                    month: today.month,
                    day: 1,
                  })
                }}
                value={target}
              />
            ) : (
              <DateStepper
                dateAriaLabel={t('page:splitSupplyPlan.filter.targetDate')}
                onChange={(d) => {
                  onTargetChange(dateToCivilDate(d))
                }}
                onTodayClick={() => {
                  onTargetChange(today)
                }}
                todayLabel={t('page:splitSupplyPlan.filter.today')}
                value={civilDateToDate(target)}
              />
            )}
          </FilterSection>
          <FilterSection label={t('page:splitSupplyPlan.filter.area')}>
            <DsSelect
              minWidth="160px"
              aria-label={t('page:splitSupplyPlan.filter.area')}
              onChange={(value) => {
                onAreaChange(
                  value === undefined || value === ALL_AREAS
                    ? undefined
                    : AREA_CODE_OPTIONS.find((a) => String(a) === value),
                )
              }}
              options={areaOptions}
              value={area === undefined ? ALL_AREAS : String(area)}
            />
          </FilterSection>
        </FilterToolbar>
      </div>
    </>
  )
}
